import React, { forwardRef, useCallback, useId, useRef } from "react";
import { FieldProps } from "../FieldBase";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputLabel } from "../InputLabel";
import { TextareaBase, TextareaBaseProps } from "../TextareaBase";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useValidation } from "../FieldBase/useValidation";

export interface MultipleTextFieldProps
  extends
    FieldProps<string, HTMLTextAreaElement>,
    Omit<TextareaBaseProps, "onChange" | "defaultValue" | "value"> {
  children?: never;
}

export const MultipleTextField = forwardRef<
  HTMLDivElement,
  MultipleTextFieldProps
>((props, rootRef) => {
  const {
    label,
    style,
    className,
    onChange,
    onFocus,
    validationMessages,
    customValidations,
    disabled,
    helperText,
    invalid,
    defaultValue,
    value: _propsValue,
    loading: propsLoading,
    readOnly: propsReadOnly,
    id,
    inputRef,
    ...restProps
  } = props;
  const ref = useRef<HTMLTextAreaElement | null>(undefined);
  const refs = useMergeRefs(inputRef, ref);

  const [value, setValue, isControlled] = useInputValue(props);

  const { loading, readOnly } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadOnly,
    onLoaded: () => {
      if (isControlled === false) {
        setValue(defaultValue);
      }
    },
  });

  const validation = useValidation({
    ref,
    validationMessages,
    customValidations,
    value,
  });
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.currentTarget;
      setValue(value);
      if (onChange) {
        onChange(value, event);
      }
    },
    [onChange, setValue],
  );
  const [touched, additionalProps] = useDitectInputTouched(onFocus);

  const internalInputId = useId();
  const inputId = id ?? internalInputId;

  return (
    <FormControl style={style} className={className} ref={rootRef}>
      <InputLabel
        aria-disabled={disabled}
        required={props.required}
        loading={loading}
        htmlFor={inputId}
      >
        {label}
      </InputLabel>
      <TextareaBase
        id={inputId}
        disabled={disabled}
        readOnly={readOnly}
        loading={loading}
        onChange={handleChange}
        value={value}
        ref={refs}
        {...restProps}
        {...additionalProps}
      />
      <FormHelperText
        invalid={!validation.valid || invalid}
        loading={loading}
        touched={touched}
        value={value}
      >
        {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          validation.message || helperText
        }
      </FormHelperText>
    </FormControl>
  );
});
